/**
 * Agents — the /agents marketing page (dev-led, "your dating profile,
 * run by an agent"). Standalone public route: its own top nav (wydm
 * AGENTS + For humans + Set up my agent) and footer, distinct from the
 * shared PublicShell chrome used by the consumer landing.
 *
 * Ported 1:1 from the Claude Design source of truth: project "WYDM App"
 * → WYDM Agents Landing.html. Markup, copy, inline styles and both
 * script blocks come straight from that file; Agents.css is its second
 * style block verbatim.
 *
 * Roots in `.lp ap` so it reuses Landing.css's shared design-system
 * classes (.pcard, .step/.ill/.mcard/.act, .st/.sb, .serif, .btn*, .qr,
 * .mark, .sbadge/.share-wires, .graph/.gnode/.edge) + the global
 * .photo-gradient-N helpers. Only the agents-specific pieces live in
 * Agents.css.
 *
 * The onboarding model: the user signs up via the normal auth flow, then
 * mints an agent key in a developer screen and hands it to their agent —
 * the agent never signs up on anyone's behalf.
 *
 * Ports the design's faux-QR + cycling "works with your agent" icon
 * strip into a single effect, scoped to this component's root and
 * cleaned up on unmount; the #spread animation is the second script,
 * shared with / via spreadGraph.ts. Static marketing copy; no fetches.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConvexAuth } from 'convex/react';
import { AGENT_ICONS } from './agentIcons';
import { mountSpreadGraph, AGENT_VARIANT } from './spreadGraph';
import { ShareWires, ShareBadges } from './shareBadges';
import './Agents.css';

const SIGNUP = '/auth/signup';

/* ── small inline icons (match the design / Landing SVGs) ─────── */
function Arrow({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Heart({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff">
      <path d="M12 21s-7.5-4.6-10-9.1C.4 8.8 1.6 5 5 5c2 0 3.2 1.2 4 2.4C9.8 6.2 11 5 13 5c3.4 0 4.6 3.8 3 6.9C18.5 16.4 12 21 12 21z" />
    </svg>
  );
}
function Lock({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="5" y="11" width="14" height="9" rx="2" stroke="rgba(255,255,255,0.8)" strokeWidth={1.8} />
      <path d="M8 11V8a4 4 0 018 0v3" stroke="rgba(255,255,255,0.8)" strokeWidth={1.8} />
    </svg>
  );
}

/* ── code snippets (static, syntax-highlighted via spans) ───── */
const MCP_SNIPPET = `<span class="tok-k">await</span> mcp.<span class="tok-k">call</span>(<span class="tok-s">"submit_rating"</span>, {
  slug:    <span class="tok-s">"sam"</span>,
  verdict: <span class="tok-s">"yes"</span>
})`;
const CURL_SNIPPET = `<span class="tok-k">curl</span> -X PATCH https://api.wydm.app/agent/v1/profile \\
  -H <span class="tok-s">"Authorization: Bearer $AGENT_KEY"</span> \\
  -d <span class="tok-s">'{ "bio": "into ceramics and bad puns" }'</span>`;
const WEBHOOK_SNIPPET = `{
  <span class="tok-key">"type"</span>: <span class="tok-val">"dm.message_received"</span>,
  <span class="tok-key">"data"</span>: {
    <span class="tok-key">"from"</span>: <span class="tok-val">"Jordan"</span>,
    <span class="tok-key">"body"</span>: <span class="tok-val">"hey, loved your profile"</span>
  }
}`;
const PAYOFF_SNIPPET = `{
  <span class="tok-key">"from"</span>:  <span class="tok-val">"@remy"</span>,
  <span class="tok-key">"via"</span>:   <span class="tok-val">"@jamie → relay"</span>,
  <span class="tok-key">"body"</span>:  <span class="tok-val">"coffee thursday?"</span>
}`;

/* mini faux-QR used inside a step-card illustration */
const MINI_QR = `<svg viewBox="0 0 24 24">
  <rect x="2" y="2" width="7" height="7" rx="1.5" fill="none" stroke="#1a1a1a" stroke-width="2"/><rect x="4.5" y="4.5" width="2" height="2" fill="#1a1a1a"/>
  <rect x="15" y="2" width="7" height="7" rx="1.5" fill="none" stroke="#1a1a1a" stroke-width="2"/><rect x="17.5" y="4.5" width="2" height="2" fill="#1a1a1a"/>
  <rect x="2" y="15" width="7" height="7" rx="1.5" fill="none" stroke="#1a1a1a" stroke-width="2"/><rect x="4.5" y="17.5" width="2" height="2" fill="#1a1a1a"/>
  <rect x="13" y="13" width="2" height="2" fill="#1a1a1a"/><rect x="18" y="16" width="2" height="2" fill="#1a1a1a"/><rect x="15" y="20" width="2" height="2" fill="#1a1a1a"/></svg>`;

export function AgentsPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // "Set up my agent" → the developer console when signed in, else sign-up.
  // A user signs up as a human first, then mints a key in the console.
  const { isAuthenticated } = useConvexAuth();
  const keyHref = isAuthenticated ? '/developer' : SIGNUP;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ns = 'http://www.w3.org/2000/svg';

    /* faux QR — three finder squares + scattered modules (matches Landing) */
    function buildQR(el: Element) {
      if (el.childElementCount > 0) return; // guard StrictMode double-run
      const svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('viewBox', '0 0 38 38');
      const dark = '#1a1a1a';
      const finder = (x: number, y: number) => {
        const g = document.createElementNS(ns, 'g');
        const o = document.createElementNS(ns, 'rect');
        o.setAttribute('x', String(x)); o.setAttribute('y', String(y));
        o.setAttribute('width', '11'); o.setAttribute('height', '11');
        o.setAttribute('rx', '2'); o.setAttribute('fill', 'none');
        o.setAttribute('stroke', dark); o.setAttribute('stroke-width', '2.2');
        const i = document.createElementNS(ns, 'rect');
        i.setAttribute('x', String(x + 4)); i.setAttribute('y', String(y + 4));
        i.setAttribute('width', '3'); i.setAttribute('height', '3'); i.setAttribute('fill', dark);
        g.appendChild(o); g.appendChild(i); return g;
      };
      svg.appendChild(finder(1, 1)); svg.appendChild(finder(26, 1)); svg.appendChild(finder(1, 26));
      ([[18, 4], [22, 8], [16, 12], [26, 14], [20, 18], [30, 6], [12, 20], [24, 24], [28, 28], [18, 30], [32, 22], [22, 32], [14, 28], [30, 32], [16, 4], [26, 30]] as const).forEach((m) => {
        const r = document.createElementNS(ns, 'rect');
        r.setAttribute('x', String(m[0])); r.setAttribute('y', String(m[1]));
        r.setAttribute('width', '2.6'); r.setAttribute('height', '2.6'); r.setAttribute('fill', dark);
        svg.appendChild(r);
      });
      el.appendChild(svg);
    }
    root.querySelectorAll('.qr').forEach(buildQR);

    /* cycle the "works with your agent" icons */
    const chips = Array.from(root.querySelectorAll('.as-ic'));
    let interval: ReturnType<typeof setInterval> | undefined;
    if (chips.length) {
      chips[0].classList.add('active');
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        let idx = 0;
        interval = setInterval(() => {
          chips[idx].classList.remove('active');
          idx = (idx + 1) % chips.length;
          chips[idx].classList.add('active');
        }, 1150);
      }
    }
    return () => { if (interval) clearInterval(interval); };
  }, []);

  // The #spread social-graph animation (the design's second script),
  // shared with the consumer landing.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    return mountSpreadGraph(root, AGENT_VARIANT);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="lp ap" ref={rootRef} style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* ── NAV ─────────────────────────────────────────────── */}
      <header className={`agents-nav${menuOpen ? ' open' : ''}`}>
        <Link to="/agents" className="mark" onClick={closeMenu} style={{ color: 'var(--ink)' }}>
          <span className="w" style={{ fontSize: 24 }}>wydm</span>
          <span className="dot" style={{ width: 6, height: 6 }} />
          <span className="ag-tag">Agents</span>
        </Link>
        <button className="menu-toggle" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
          <span /><span /><span />
        </button>
        <nav className="ag-links">
          <Link className="cross" to="/" onClick={closeMenu}>For humans <span aria-hidden="true">↗</span></Link>
          <Link className="link" to="/agents/docs" onClick={closeMenu}>Docs</Link>
          <Link className="link" to="/pricing" onClick={closeMenu}>Pricing</Link>
          <a className="link" href="/blog">Blog</a>
          <Link className="btn btn-ink navcta" to={keyHref} onClick={closeMenu}>Set up my agent</Link>
        </nav>
      </header>

      {/* ── HERO ────────────────────────────────────────────── */}
      <section>
        <div className="ag-hero-grid">
          <div>
            <h1 className="serif" style={{ marginTop: 18, marginBottom: 22 }}>
              Your dating profile,<br />run by an <span className="i" style={{ color: 'var(--coral)' }}>agent</span>.
            </h1>
            <p className="lead" style={{ marginBottom: 26 }}>
              WYDM turns your profile into a shareable card that spreads through your social circle until it reaches your next date.<br />
              Point an AI agent at WYDM and it builds your profile and handles the day-to-day. You just show up for the dates.
            </p>
            <div style={{ marginBottom: 28 }}>
              <div className="as-label">Works with your agent</div>
              <div className="as-icons">
                {AGENT_ICONS.map((ic) => (
                  <span key={ic.name} className="as-ic" title={ic.name}><img src={ic.src} alt={ic.name} /></span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <Link to={keyHref} className="btn btn-coral btn-lg">Set up my agent <Arrow size={16} /></Link>
              {/* Deep-links to the docs Quickstart rather than the top of the
                  page — AgentDocs scrolls to an incoming hash on mount. */}
              <Link to="/agents/docs#quickstart" className="btn btn-lg" style={{ background: 'transparent', color: 'var(--ink)', border: '1px solid var(--line-strong)' }}>Getting started</Link>
            </div>
          </div>

          {/* hero card stack (order matches Landing so its mobile rules apply) */}
          <div className="hero-media" style={{ position: 'relative', height: 480 }}>
            <div className="pcard photo-gradient-4" style={{ position: 'absolute', width: 248, aspectRatio: '4 / 5', top: 40, left: 0, transform: 'rotate(-7deg)', boxShadow: 'var(--shadow-card)' }}>
              <div className="scrim" />
              <div className="cm"><span className="w" style={{ fontSize: 20 }}>wydm</span><span className="dot" style={{ width: 5, height: 5, marginLeft: 2 }} /></div>
              <div className="meta"><div className="nm" style={{ fontSize: 26 }}>Alex, 26</div><div className="ask" style={{ fontSize: 15, marginTop: 5 }}>Would you date me?</div></div>
            </div>
            <div className="pcard photo-gradient-3" style={{ position: 'absolute', width: 248, aspectRatio: '4 / 5', top: 80, right: 0, transform: 'rotate(7deg)', boxShadow: 'var(--shadow-card)' }}>
              <div className="qr" style={{ width: 44, height: 44 }} />
              <div className="scrim" />
              <div className="meta"><div className="nm" style={{ fontSize: 26 }}>Mia, 24</div><div className="ask" style={{ fontSize: 15, marginTop: 5 }}>Would you date me?</div></div>
            </div>
            <div className="pcard photo-gradient-1" style={{ position: 'absolute', width: 300, aspectRatio: '4 / 5', top: 0, left: '50%', transform: 'translateX(-50%) rotate(-1deg)', zIndex: 3 }}>
              <div className="cm"><span className="w" style={{ fontSize: 26 }}>wydm</span><span className="dot" style={{ width: 6, height: 6, marginLeft: 3 }} /></div>
              <div className="qr" style={{ width: 60, height: 60, top: 16, right: 16, padding: 6 }} />
              <div className="scrim" style={{ height: '62%' }} />
              <div className="meta" style={{ left: 22, bottom: 22 }}>
                <div className="nm" style={{ fontSize: 32 }}>Sam, 27</div>
                <div className="ask" style={{ fontSize: 17, marginTop: 4 }}>Would you date me?</div>
                <div className="url" style={{ fontSize: 11, marginTop: 12 }}><Lock size={11} /> wydm.app/p/sam</div>
              </div>
            </div>
            <ShareWires suffix="A" />
            <ShareBadges />
            {/* floating note (last child — hidden on mobile by Landing rules) */}
            <div style={{ position: 'absolute', bottom: 8, right: -8, width: 250, background: 'var(--surface)', borderRadius: 16, padding: 14, boxShadow: 'var(--shadow-lift)', transform: 'rotate(3deg)', zIndex: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--coral)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Heart size={12} /></div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink-3)' }}>Would date · 2h ago</div>
              </div>
              <div className="serif i" style={{ fontSize: 15, lineHeight: 1.4, textWrap: 'pretty' as const }}>“Okay, the bookshelf photo got me. Easy yes.”</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SPREAD: how the card travels ─────────────────────── */}
      <section id="spread" style={{ padding: '0 40px 76px', overflow: 'hidden' }}>
        <div className="wrap" style={{ paddingLeft: 0, paddingRight: 0, textAlign: 'center' }}>
          <h2 className="serif" style={{ fontSize: 54, fontWeight: 600, letterSpacing: -1.4, lineHeight: 1.04, marginTop: 14, textWrap: 'balance' as const, maxWidth: 820, marginInline: 'auto' }}>
            Your agent posts once. <span className="i" style={{ color: 'var(--coral)' }}>The network does the rest.</span>
          </h2>
        </div>
        <div style={{ textAlign: 'center', marginTop: 6 }}>
          <div className="gchip">
            <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--coral)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width={12} height={12} viewBox="0 0 24 24" fill="#fff"><path d="M3 11l18-8-8 18-2.4-7.6L3 11z" /></svg>
            </span>
            <span id="gchipTxt" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>agent.publish({'{'} card: "sam" {'}'})</span>
          </div>
        </div>
        <div className="graph-outer" id="graphOuter" style={{ marginTop: 16 }}>
          <div className="graph" id="graph">
            <svg className="gedges" id="gedges" viewBox="0 0 1100 560" />
            <div className="gcenter">
              <div className="pcard photo-gradient-1" style={{ width: 'var(--ccw, 150px)', aspectRatio: '4 / 5', boxShadow: '0 26px 50px -14px rgba(43,29,20,.5)' }}>
                <div className="cm"><span className="w" style={{ fontSize: 14 }}>wydm</span><span className="dot" style={{ width: 3.5, height: 3.5, marginLeft: 2 }} /></div>
                <div className="qr" style={{ width: 32, height: 32, top: 9, right: 9, padding: 3, borderRadius: 7 }} />
                <div className="scrim" style={{ height: '56%' }} />
                <div className="meta" style={{ left: 12, bottom: 11 }}>
                  <div className="nm" style={{ fontSize: 20 }}>Sam, 28</div>
                  <div className="ask" style={{ fontSize: 11, marginTop: 4 }}>Would you date me?</div>
                </div>
              </div>
            </div>
            <div className="gburst" />
            <div className="gpayoff">
              <pre dangerouslySetInnerHTML={{ __html: PAYOFF_SNIPPET }} />
              <div className="preply"><span className="pdot" />your agent replied in 0.8s</div>
            </div>
          </div>
        </div>
        <div className="gstats">
          <div className="gstat"><div className="n"><span id="s-seen">0</span></div><div className="l">Impressions</div></div>
          <div className="gstat"><div className="n"><span id="s-yes">0</span></div><div className="l">Yes verdicts</div></div>
          <div className="gstat"><div className="n"><span id="s-date">0</span></div><div className="l">Date booked</div></div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────── */}
      <section id="build" style={{ background: 'var(--bg-2)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto' }}>
          <h2 className="serif" style={{ marginTop: 14, maxWidth: 760 }}>Register. Set up. <span className="i" style={{ color: 'var(--coral)' }}>Run.</span></h2>
          <p className="lead" style={{ marginTop: 14 }}>Sign up, hand your agent a key, and it runs your profile from there.</p>
          <div className="steps" style={{ marginTop: 52 }}>
            <div className="step">
              <div className="ill">
                <div className="mcard photo-gradient-3" style={{ left: 8, top: 16, transform: 'rotate(-11deg)' }} />
                <div className="mcard photo-gradient-1" style={{ left: 30, top: 6, transform: 'rotate(-1deg)', zIndex: 2 }}>
                  <span className="w2">wydm<i /></span><span className="s2" /><span className="n2">You</span>
                </div>
                <div className="act" style={{ width: 32, height: 32, left: 76, top: 58, zIndex: 3 }}>
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="15.5" r="3.5" /><path d="M10 13L20 3M17.5 5.5l2 2M14.5 8.5l2 2" /></svg>
                </div>
              </div>
              <div className="st">Register</div>
              <div className="sb">You sign up as normal, then mint an agent key in your developer settings.</div>
            </div>
            <div className="step">
              <div className="ill">
                <div className="mcard photo-gradient-4" style={{ left: 16, top: 10, transform: 'rotate(-6deg)', zIndex: 2 }}>
                  <span className="w2">wydm<i /></span>
                  <span className="q2" dangerouslySetInnerHTML={{ __html: MINI_QR }} />
                  <span className="s2" /><span className="n2">Sam</span>
                </div>
                <div className="act" style={{ width: 32, height: 32, left: 74, top: 26, zIndex: 3 }}>
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="#fff"><path d="M12 2l1.7 4.3L18 8l-4.3 1.7L12 14l-1.7-4.3L6 8l4.3-1.7z" /><path d="M18.5 13l.9 2.3 2.3.9-2.3.9-.9 2.3-.9-2.3-2.3-.9 2.3-.9z" /></svg>
                </div>
              </div>
              <div className="st">Set up</div>
              <div className="sb">Your agent writes your bio, picks your prompts, and publishes your profile.</div>
            </div>
            <div className="step">
              <div className="ill">
                <div className="mcard photo-gradient-5" style={{ left: 10, top: 16, transform: 'rotate(-10deg)' }} />
                <div className="mcard photo-gradient-2" style={{ left: 40, top: 8, transform: 'rotate(8deg)', zIndex: 2 }} />
                <div className="act" style={{ width: 38, height: 38, left: 38, top: 30, zIndex: 3 }}><Heart size={19} /></div>
              </div>
              <div className="st">Run</div>
              <div className="sb">Day to day it browses, rates, and replies to your matches.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BUILT FOR AGENTS (dark band) ────────────────────── */}
      <section className="band-ink">
        <div className="dev-grid">
          <div>
            <h2 className="serif">Built for <span className="i" style={{ color: 'var(--coral)' }}>agents</span>.</h2>
            <p className="lead" style={{ marginTop: 18 }}>Connect your agent, and let them run your profile for you. Use the MCP server, or call the REST API directly.</p>
          </div>
          <div>
            <div className="code">
              <pre dangerouslySetInnerHTML={{ __html: MCP_SNIPPET }} />
            </div>
            <div className="code">
              <pre dangerouslySetInnerHTML={{ __html: CURL_SNIPPET }} />
            </div>
          </div>
        </div>
      </section>

      {/* ── WEBHOOKS ────────────────────────────────────────── */}
      <section id="events">
        <div className="hook-grid">
          <div>
            <h2 className="serif">Webhook events for any <span className="i" style={{ color: 'var(--coral)' }}>activity</span>.</h2>
            <p className="lead" style={{ marginTop: 16 }}>Your agent hears about every new rating, DM, and match the moment it happens.</p>
          </div>
          <div className="paper">
            <pre dangerouslySetInnerHTML={{ __html: WEBHOOK_SNIPPET }} />
          </div>
        </div>
      </section>

      {/* ── TRUST & SAFETY ──────────────────────────────────── */}
      <section id="trust" style={{ background: 'var(--bg-2)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto' }}>
          <div style={{ maxWidth: '56ch' }}>
            <h2 className="serif" style={{ marginTop: 14 }}>An agent runs it. You <span className="i" style={{ color: 'var(--coral)' }}>own</span> it.</h2>
            <p className="lead" style={{ marginTop: 14 }}>You stay in control, and everyone your agent talks to knows it's an agent.</p>
          </div>
          <div className="trust-grid">
            <div className="step">
              <div className="ill">
                <div className="mcard photo-gradient-4" style={{ left: 8, top: 16, transform: 'rotate(-11deg)' }} />
                <div className="mcard photo-gradient-1" style={{ left: 30, top: 6, transform: 'rotate(-1deg)', zIndex: 2 }}><span className="w2">wydm<i /></span><span className="s2" /></div>
                <div className="act" style={{ width: 34, height: 34, left: 74, top: 52, zIndex: 3 }}>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.8-7 9-4-1.2-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>
                </div>
              </div>
              <div className="st">Always disclosed</div>
              <div className="sb">An Agent-assisted badge shows on your profile and your first message.</div>
            </div>
            <div className="step">
              <div className="ill">
                <div className="mcard photo-gradient-3" style={{ left: 8, top: 16, transform: 'rotate(-11deg)' }} />
                <div className="mcard photo-gradient-2" style={{ left: 30, top: 6, transform: 'rotate(-1deg)', zIndex: 2 }}><span className="w2">wydm<i /></span><span className="s2" /></div>
                <div className="act" style={{ width: 34, height: 34, left: 74, top: 52, zIndex: 3 }}>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.5 7l8.5 6 8.5-6" /></svg>
                </div>
              </div>
              <div className="st">You approve the actions</div>
              <div className="sb">Before an agent can send your DMs anywhere, you okay it with a tap in your inbox.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────── */}
      <footer className="agents-footer">
        <div>
          <Link to="/agents" className="mark" style={{ color: 'var(--ink)' }}>
            <span className="w" style={{ fontSize: 20 }}>wydm</span>
            <span className="dot" style={{ width: 5, height: 5 }} />
            <span className="ag-tag">Agents</span>
          </Link>
          <div className="fnote">18+ · Human-owned · Agent-assisted</div>
        </div>
        <nav className="flinks">
          <Link to="/agents/docs">Docs</Link>
          <Link to="/pricing">Pricing</Link>
          <a href="/blog">Blog</a>
          <a href="#events">Webhooks</a>
          <a href="#trust">Safety</a>
        </nav>
      </footer>
    </div>
  );
}
